/**
 * ConsentDocViewer — modal that fetches and displays a published consent
 * document by type using the existing /api/consent/documents/{type} endpoint
 * (unauthenticated GET, so Triveny's home footer can call it without a session).
 *
 * Used by:
 *   - TrivenyHome footer Privacy + AI Disclaimers links (Phase 1, view-only)
 *
 * The version + content are managed via the existing consent_management admin
 * endpoints — see app/routers/consent_management.py. New versions activated via
 * POST /api/consent/admin/documents/{id}/activate; the trigger ensures only
 * one active version per document_type at any time.
 *
 * Phase 2 will extend this to render a "Confirm" CTA that POSTs to
 * /api/consent/sign with the platform JWT, recording acceptance in the new
 * platform_shared.tenant_consent_signatures table (see migration 389).
 */

import React, { useEffect, useState } from 'react';
import { X, Loader2, AlertCircle, FileText } from 'lucide-react';
import { config } from '@/src/config';
import { tvTokens } from '@/src/platform/shared/components/triveny';

export type ConsentDocType =
  | 'terms_of_use'
  | 'hipaa_authorization'
  | 'privacy_policy'
  | 'ai_disclaimers';

interface ConsentDocResponse {
  id: number;
  document_type: string;
  version: string;
  title: string;
  content: string;
  effective_date?: string;
  is_active?: boolean;
}

export interface ConsentDocViewerProps {
  open: boolean;
  documentType: ConsentDocType;
  onClose: () => void;
}

/**
 * Minimal markdown renderer — handles the headings + bold + paragraphs that
 * our seeded Privacy Policy + AI Disclaimers content uses. We avoid pulling
 * in react-markdown because the rendered surface is small and trusted
 * (admin-authored markdown only, no untrusted user input).
 */
function renderMarkdown(md: string): React.ReactNode {
  const lines = md.split('\n');
  const out: React.ReactNode[] = [];
  let listBuf: string[] = [];

  const flushList = () => {
    if (listBuf.length === 0) return;
    out.push(
      <ul key={`list-${out.length}`} style={{ listStyle: 'disc outside', paddingLeft: 22, margin: '8px 0' }}>
        {listBuf.map((item, i) => (
          <li key={i} style={{ marginBottom: 4 }} dangerouslySetInnerHTML={{ __html: inline(item) }} />
        ))}
      </ul>,
    );
    listBuf = [];
  };

  const inline = (s: string): string =>
    s
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code style="background:#f3f3ef;padding:1px 5px;border-radius:3px;font-family:JetBrains Mono,monospace;font-size:12px">$1</code>');

  lines.forEach((line, idx) => {
    if (line.startsWith('# ')) {
      flushList();
      out.push(
        <h1 key={idx} style={{ fontFamily: tvTokens.fontDisplay, fontSize: 26, fontWeight: 600, letterSpacing: '-0.025em', margin: '20px 0 12px', color: tvTokens.ink }}>
          {line.slice(2)}
        </h1>,
      );
    } else if (line.startsWith('## ')) {
      flushList();
      out.push(
        <h2 key={idx} style={{ fontFamily: tvTokens.fontDisplay, fontSize: 18, fontWeight: 600, letterSpacing: '-0.015em', margin: '22px 0 8px', color: tvTokens.ink }}>
          {line.slice(3)}
        </h2>,
      );
    } else if (line.startsWith('- ')) {
      listBuf.push(line.slice(2));
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      out.push(
        <p key={idx} style={{ fontSize: 14, lineHeight: 1.6, margin: '8px 0', color: tvTokens.ink700 }} dangerouslySetInnerHTML={{ __html: inline(line) }} />,
      );
    }
  });
  flushList();
  return <>{out}</>;
}

export default function ConsentDocViewer({ open, documentType, onClose }: ConsentDocViewerProps) {
  const [doc, setDoc] = useState<ConsentDocResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDoc(null);
    (async () => {
      try {
        const res = await fetch(
          `${config.weaveinApi.baseUrl}/api/consent/documents/${encodeURIComponent(documentType)}`,
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const body = (await res.json()) as ConsentDocResponse;
        if (!cancelled) setDoc(body);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load document');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, documentType]);

  // Esc closes
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(20,23,28,0.42)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: 'min(820px, 100%)',
          maxHeight: '88vh',
          background: tvTokens.surface,
          borderRadius: tvTokens.rXl,
          border: `1px solid ${tvTokens.border}`,
          boxShadow: '0 24px 60px -20px rgba(20,23,28,0.35)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 18px',
            borderBottom: `1px solid ${tvTokens.borderMuted}`,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: tvTokens.r,
              background: tvTokens.ink,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FileText size={14} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: tvTokens.fontMono,
                fontSize: 10,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: tvTokens.ink500,
              }}
            >
              Legal · Triveny.ai
            </div>
            <div
              style={{
                fontFamily: tvTokens.fontDisplay,
                fontSize: 16,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: tvTokens.ink,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {doc?.title || 'Loading…'}
              {doc?.version && (
                <span
                  style={{
                    fontFamily: tvTokens.fontMono,
                    fontSize: 11,
                    fontWeight: 500,
                    color: tvTokens.ink500,
                    marginLeft: 8,
                  }}
                >
                  v{doc.version}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 28,
              height: 28,
              borderRadius: tvTokens.r,
              border: `1px solid ${tvTokens.border}`,
              background: tvTokens.surface,
              color: tvTokens.ink500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 24px', overflowY: 'auto', flex: 1 }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: tvTokens.ink500, fontSize: 13 }}>
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          )}
          {error && !loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#c2410c', fontSize: 13 }}>
              <AlertCircle size={16} /> {error}
            </div>
          )}
          {doc && !loading && !error && renderMarkdown(doc.content)}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 18px',
            borderTop: `1px solid ${tvTokens.borderMuted}`,
            background: tvTokens.paper,
          }}
        >
          <span style={{ fontFamily: tvTokens.fontMono, fontSize: 10, color: tvTokens.ink400 }}>
            esc to close
          </span>
          {doc?.effective_date && (
            <span style={{ fontSize: 11, color: tvTokens.ink500 }}>
              Effective {new Date(doc.effective_date).toLocaleDateString()}
            </span>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 14px',
                borderRadius: tvTokens.r,
                background: tvTokens.ink,
                color: '#fff',
                border: 0,
                fontFamily: tvTokens.fontSans,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
